import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Calendar, Clock, MapPin, Edit, Trash2, X, 
  Dumbbell, Trophy, Users as UsersIcon, MessageSquare 
} from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sessionTypes = [
  { value: 'training', label: 'Training', icon: Dumbbell, color: 'text-green-400' },
  { value: 'game', label: 'Game', icon: Trophy, color: 'text-red-400' },
  { value: 'tournament', label: 'Tournament', icon: Trophy, color: 'text-purple-400' },
  { value: 'meeting', label: 'Meeting', icon: UsersIcon, color: 'text-blue-400' },
];

const TeamSessions = () => {
  const { currentTeam, isManager } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('upcoming');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    session_type: 'training',
    date: '',
    time: '15:00',
    duration_minutes: 90,
    location: '',
    description: ''
  });

  useEffect(() => {
    if (currentTeam) {
      fetchSessions();
    }
  }, [currentTeam]);

  // Check if should open dialog on load
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setDialogOpen(true);
      setSearchParams({});
    }
  }, [searchParams]);

  const fetchSessions = async () => {
    const token = localStorage.getItem('team_token');
    if (!token) return;

    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/sessions`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (session = null) => {
    if (session) {
      const sessionDate = parseISO(session.date);
      setFormData({
        title: session.title,
        session_type: session.session_type,
        date: format(sessionDate, 'yyyy-MM-dd'),
        time: format(sessionDate, 'HH:mm'),
        duration_minutes: session.duration_minutes,
        location: session.location || '',
        description: session.description || ''
      });
      setEditingSession(session);
    } else {
      setFormData({
        title: '',
        session_type: 'training',
        date: format(new Date(), 'yyyy-MM-dd'),
        time: '15:00',
        duration_minutes: 90,
        location: '',
        description: ''
      });
      setEditingSession(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSession(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('team_token');
    if (!token) return;

    try {
      const dateTime = `${formData.date}T${formData.time}:00Z`;
      
      const payload = {
        title: formData.title,
        session_type: formData.session_type,
        date: dateTime,
        duration_minutes: parseInt(formData.duration_minutes),
        location: formData.location || null,
        description: formData.description || null
      };

      const url = editingSession 
        ? `${API_URL}/api/clubs/team/${currentTeam.team_id}/sessions/${editingSession.id}`
        : `${API_URL}/api/clubs/team/${currentTeam.team_id}/sessions`;

      const response = await fetch(url, {
        method: editingSession ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save session');
      }

      toast.success(editingSession ? 'Session updated!' : 'Session created!');
      handleCloseDialog();
      fetchSessions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (sessionId) => {
    if (!confirm('Are you sure you want to cancel this session?')) return;

    const token = localStorage.getItem('team_token');
    if (!token) return;

    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/sessions/${sessionId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to cancel session');
      }

      toast.success('Session cancelled');
      fetchSessions();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getSessionTypeInfo = (type) => {
    return sessionTypes.find(t => t.value === type) || sessionTypes[0];
  };

  const getDateLabel = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return { label: 'Today', class: 'text-green-400' };
    if (isTomorrow(date)) return { label: 'Tomorrow', class: 'text-blue-400' };
    if (isPast(date)) return { label: format(date, 'MMM d'), class: 'text-muted-foreground' };
    return { label: format(date, 'MMM d'), class: 'text-foreground' };
  };

  const filteredSessions = sessions.filter(session => {
    const date = parseISO(session.date);
    if (filter === 'upcoming') return !isPast(date) && session.status !== 'cancelled';
    if (filter === 'past') return isPast(date) || session.status === 'cancelled';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-sessions">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading">Sessions</h1>
          <p className="text-muted-foreground mt-1">
            Manage training, games, and tournaments
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="new-session-btn">
          <Plus size={18} className="mr-2" />
          New Session
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <Button 
          variant={filter === 'upcoming' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('upcoming')}
        >
          Upcoming
        </Button>
        <Button 
          variant={filter === 'past' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('past')}
        >
          Past
        </Button>
        <Button 
          variant={filter === 'all' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('all')}
        >
          All
        </Button>
      </div>

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Sessions Found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {filter === 'upcoming' ? 'No upcoming sessions scheduled' : 'No sessions in this category'}
            </p>
            <Button onClick={() => handleOpenDialog()}>
              <Plus size={18} className="mr-2" />
              Schedule a Session
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredSessions.map((session) => {
            const typeInfo = getSessionTypeInfo(session.session_type);
            const dateInfo = getDateLabel(session.date);
            const TypeIcon = typeInfo.icon;
            
            return (
              <Card 
                key={session.id} 
                className={`transition-colors ${session.status === 'cancelled' ? 'opacity-60' : ''}`}
                data-testid={`session-card-${session.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Type Icon */}
                    <div className={`p-3 rounded-lg bg-muted ${typeInfo.color}`}>
                      <TypeIcon size={24} />
                    </div>

                    {/* Session Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium">{session.title}</h3>
                        {session.status === 'cancelled' && (
                          <span className="px-2 py-0.5 bg-red-500/10 text-red-400 text-xs rounded">
                            Cancelled
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                        <span className={`flex items-center gap-1 ${dateInfo.class}`}>
                          <Calendar size={14} />
                          {dateInfo.label} • {format(parseISO(session.date), 'HH:mm')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {session.duration_minutes} min
                        </span>
                        {session.location && (
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {session.location}
                          </span>
                        )}
                      </div>
                      {session.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-1">
                          {session.description}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    {session.status !== 'cancelled' && (
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleOpenDialog(session)}
                          data-testid={`edit-session-${session.id}`}
                        >
                          <Edit size={18} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleCancel(session.id)}
                          data-testid={`cancel-session-${session.id}`}
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingSession ? 'Edit Session' : 'New Session'}
            </DialogTitle>
            <DialogDescription>
              {editingSession 
                ? 'Update session details. Team members will be notified of changes.'
                : 'Schedule a new session. Team members will be notified.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Weekly Training, Championship Game"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="session-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select 
                value={formData.session_type} 
                onValueChange={(v) => setFormData({ ...formData, session_type: v })}
              >
                <SelectTrigger data-testid="session-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sessionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon size={16} className={type.color} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="session-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                  data-testid="session-time-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (min)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="15"
                  max="480"
                  step="15"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                  required
                  data-testid="session-duration-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder="e.g., Main Field"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  data-testid="session-location-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Add notes or details about this session..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="session-description-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-session-btn">
                {saving ? 'Saving...' : (editingSession ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamSessions;
